import * as XLSX from "xlsx";  
import { getApplicationsByContestants } from "../api/application";  
import { getProvince } from "../api/competition";  
  
// Adjust column widths for prettiness  
const adjustColumnWidths = (data) => {  
  const wscols = [];  
  const columns = Object.keys(data[0]);  
  columns.forEach((column) => {  
    let maxLength = column.length;  
    data.forEach((row) => {  
      const value = row[column] ? row[column].toString() : "";  
      maxLength = Math.max(maxLength, value.length);  
    });  
    wscols.push({ wch: maxLength + 2 });  
  });  
  return wscols;  
};  
  
function buildApplicationsMap(applications) {  
  // Now: store both style(s) AND instrument(s) for instrumentals  
  // Map: { [contestantId]: { [eventName]: {styles: [...], instruments: [...] }}}  
  const byContestant = {};  
  applications.forEach(app => {  
    if (!app.contestant || !app.event) return;  
    const cId = app.contestant._id?.toString?.() ?? app.contestant;  
    byContestant[cId] = byContestant[cId] || {};  
    byContestant[cId][app.event.name] = byContestant[cId][app.event.name] || {styles: [], instruments: []};  
    byContestant[cId][app.event.name].styles.push(app.event.style);  
    if (app.event.name.toLowerCase().includes("instrumental")) {  
      byContestant[cId][app.event.name].instruments.push(app.instrument || ""); // Defensive: blank if missing  
    }  
  });  
  return byContestant;  
}  
  
function getAllEventNames(applications) {  
  const set = new Set();  
  applications.forEach(app => {  
    if (app.event && app.event.name) set.add(app.event.name);  
  });  
  return Array.from(set);  
}  
  
const formatDate = (isoDate) => {  
  const d = new Date(isoDate);  
  const day = d.getDate();  
  const month = d.toLocaleString('default', { month: 'long' });  
  const year = d.getFullYear();  
  return `${day} ${month} ${year}`;  
};  

function buildAuditionDateString(province) {  
  if (!province || typeof province !== 'object') return "";  
  const name = province.province || "";  
  const date = province.date ? formatDate(province.date) : "";  
  const location = province.location || "";  
  return [name, date, location].filter(Boolean).join(" - ");  
}  
  
export const exportContestants = async (contestants, provinceId) => {  
  const contestantIds = contestants.map(c => c._id.toString());  
  const applications = await getApplicationsByContestants(contestantIds);  
  
  const appsMap = buildApplicationsMap(applications);  
  const eventNames = getAllEventNames(applications);  

  const paidStatusMap = {};
  contestantIds.forEach(cId => {  
    const apps = applications.filter(app => {  
      const id = app.contestant?._id?.toString?.() ?? app.contestant;  
      return id === cId;  
    });  
    paidStatusMap[cId] = apps.length && apps.every(app => app.paid)  
      ? "Paid"  
      : "Not Paid";  
  });
  
  // === NEW: We want to insert columns after "Instrumental" event columns.  
  // So build column order up front:  
  let baseColumns = [  
    "Audition Date",  
    "Contestant Number",  
    "First Name",  
    "Last Name",  
    "Address",  
    "Gender",  
    "ID Number",  
    "Date of Birth",  
    "Passport Number",  
    "Age",  
    "Cell Number",  
    "Work Number",  
    "Email",  
    "Coach/Parent Name",  
    "Coach/Parent Email",  
  ];  
  // Add event columns, and (if instrumental) the matching instrument column immediately after  
  let eventColumnSpecs = [];  
  eventNames.forEach(eventName => {  
    eventColumnSpecs.push({ name: eventName });  
    if (eventName.toLowerCase().includes("instrumental")) {  
      eventColumnSpecs.push({ name: `${eventName} - Instrument` });  
    }  
  });  
  
  // Build data rows  
  const data = contestants.map((contestant) => {  
    const {  
      contestant_number,  
      first_name,  
      last_name,  
      address,  
      date_of_birth,  
      age,  
      gender,  
      id_number,  
      passport_number,  
      cell_number,  
      work_number,  
      email,  
      ice_contact_name,  
      ice_contact_email,  
    } = contestant;  
  
    // Build base row:  
    const row = {  
      "Audition Date": buildAuditionDateString(contestant.province),  
      "Contestant Number": contestant_number,  
      "First Name": first_name,  
      "Last Name": last_name,  
      "Address": `${address[0].street_1} ${address[0].city}, ${address[0].country} ${address[0].province} ${address[0].postal_code}`,  
      "Gender": gender,  
      "ID Number": id_number,  
      "Date of Birth": date_of_birth,  
      "Passport Number": passport_number,  
      "Age": age,  
      "Cell Number": cell_number,  
      "Work Number": work_number,  
      "Email": email,  
      "Coach/Parent Name": ice_contact_name,  
      "Coach/Parent Email": ice_contact_email,  
      "Paid": paidStatusMap[contestant._id.toString()] || "Not Paid"  
    };  
  
    // Applications for this contestant  
    const contestantApplications = appsMap[contestant._id.toString()] || {};  
  
    // Add event columns  
    eventColumnSpecs.forEach(spec => {  
      if (spec.name.endsWith(" - Instrument")) {  
        // Instrument column, so get matching event name  
        const eventName = spec.name.replace(" - Instrument", "");  
        const apps = contestantApplications[eventName];  
        // Join non-empty instruments, or blank  
        row[spec.name] = apps && apps.instruments.length  
          ? apps.instruments.filter(Boolean).join(', ')  
          : "";  
      } else {  
        // Regular event column, show styles  
        const apps = contestantApplications[spec.name];  
        row[spec.name] = apps && apps.styles.length  
          ? apps.styles.join(', ')  
          : "";  
      }  
    });  
  
    return row;  
  });  
  
  // Build column order for worksheet  
  const allColumns = [  
    ...baseColumns,  
    ...eventColumnSpecs.map(x => x.name),
    "Paid"
  ];  
  
  // Order data: to ensure column order, map rows to only these columns  
  const orderedData = data.map(row =>  
    Object.fromEntries(allColumns.map(col => [col, row[col] || ""]))  
  );  
  
  const worksheet = XLSX.utils.json_to_sheet(orderedData);  
  
  worksheet["!cols"] = adjustColumnWidths(orderedData);  
  
  const workbook = XLSX.utils.book_new();  
  XLSX.utils.book_append_sheet(workbook, worksheet, 'ContestantsSheet');  
  XLSX.writeFile(workbook, "Contestants.xlsx");  
};  